import React, { useState, useEffect } from 'react';
import ShareService from '../../../services/shareService';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
// Basic E.164-style validation after stripping common separators
const isValidPhoneNumber = (raw) => {
  if (!raw || typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  // Allow digits, spaces, dashes, parentheses and leading +
  const cleaned = trimmed.replace(/[\s\-()]/g, '');
  const hasPlus = cleaned.startsWith('+');
  const digits = hasPlus ? cleaned.slice(1) : cleaned;
  if (!/^\d{10,15}$/.test(digits)) return false;
  // basic guard against starting with 0 in national part
  if (digits[0] === '0') return false;
  return true;
};

// Indian-style pincode: exactly 6 digits, not starting with 0
const isValidPin = (raw) => {
  if (!raw || typeof raw !== 'string') return false;
  const digits = raw.trim().replace(/\D/g, '');
  if (!/^\d{6}$/.test(digits)) return false;
  if (digits[0] === '0') return false;
  return true;
};

const ShareModal = ({ isOpen, onClose, onConfirm, buildImageBlob }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [dataConsent, setDataConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isLandscape, setIsLandscape] = useState(false);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setMobile('');
      setPin('');
      setMarketingConsent(false);
      setDataConsent(false);
      setErrors({});
      setLoading(false);
      setServerError('');
      setSuccessMsg('');
    }
  }, [isOpen]);

  useEffect(() => {
    const updateLayoutFlags = () => {
      const w = typeof window !== 'undefined' ? window.innerWidth : 0;
      const h = typeof window !== 'undefined' ? window.innerHeight : 0;
      const landscape = w >= h;
      setIsLandscape(landscape);
      // Consider compact if viewport height is small (landscape phones/tablets)
      setIsCompact(h <= 560);
    };
    updateLayoutFlags();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateLayoutFlags);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateLayoutFlags);
      }
    };
  }, []);

  const validate = () => {
    const next = {};
    if (!name.trim()) next.name = 'Name is required';
    if (!email.trim()) next.email = 'Email is required';
    else if (!emailRegex.test(email.trim())) next.email = 'Invalid email';
    if (!mobile.trim()) next.mobile = 'Contact number is required';
    else if (!isValidPhoneNumber(mobile)) next.mobile = 'Enter a valid phone number';
    if (!pin.trim()) next.pin = 'Pincode is required';
    else if (!isValidPin(pin)) next.pin = 'Enter a valid 6-digit pincode';
    if (!marketingConsent) next.marketingConsent = 'Please consent to receiving marketing emails';
    if (!dataConsent) next.dataConsent = 'Please accept data processing and policies';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;
    try {
      setLoading(true);
      const blob = typeof buildImageBlob === 'function' ? await buildImageBlob() : null;
      const file = blob ? new File([blob], 'Pic.jpg', { type: blob.type || 'image/jpeg' }) : null;
      const res = await ShareService.shareModel({
        name: name.trim(),
        email: email.trim(),
        contactNumber: mobile.trim(),
        pin: pin.trim(),
        toc: !!dataConsent,
        marketing: !!marketingConsent,
        file,
      });
      const friendly = `Thanks ${name.trim()}! We'll share your visual to ${email.trim()}. Please check your inbox shortly. If you don't see it, check your spam folder.`;
      const msg =  friendly;
      setSuccessMsg(msg);
      if (typeof window !== 'undefined' && window?.alert) {
        window.alert(msg);
      }
      if (onConfirm) onConfirm({ name, email, mobile, pin, marketingConsent, dataConsent });
    } catch (err) {
      const details = err?.message ? ` (${err.message})` : '';
      const failMsg = `We couldn't share your visual right now. Please verify your details and try again.${details}`;
      setServerError(failMsg);
      if (typeof window !== 'undefined' && window?.alert) {
        window.alert(failMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50">
      <div role="dialog" aria-modal="true" className="bg-white text-black rounded-2xl shadow-xl w-[92%] md:w-[80%] lg:w-[60%] xl:w-[50%] max-w-[720px] max-h-[85vh] h-auto p-4 md:p-6 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-3 md:mb-4 shrink-0">
          <h2 className="text-xl font-semibold font-brand">Share your room</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto">
        {successMsg ? (
          <div className="space-y-4 pr-1">
            <div className="rounded-lg border border-green-200 bg-green-50 text-green-800 p-3">
              {successMsg}
            </div>
            <div className="flex justify-end">
              <button type="button" className="px-4 py-2 rounded-xl bg-black text-white" onClick={onClose}>Close</button>
            </div>
          </div>
        ) : (
          <form className={`${isCompact ? 'space-y-3' : 'space-y-4'} pr-1`} onSubmit={handleSubmit}>
            <div className={`grid gap-3 md:gap-4 ${isLandscape ? 'md:grid-cols-3' : 'grid-cols-1'}`}>
              <div className="flex flex-col md:col-span-2">
                <label className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium mb-1`}>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`border rounded-lg px-3 ${isCompact ? 'py-1.5' : 'py-2'} focus:outline-none focus:ring-2 focus:ring-black/30 text-black placeholder-gray-500`}
                  placeholder="Your name"
                  disabled={loading}
                />
                {errors.name && <div className="text-red-600 text-sm mt-1">{errors.name}</div>}
              </div>

              <div className="flex flex-col md:col-span-1">
                <label className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium mb-1`}>Mobile</label>
                <input
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  className={`border rounded-lg px-3 ${isCompact ? 'py-1.5' : 'py-2'} focus:outline-none focus:ring-2 focus:ring-black/30 text-black placeholder-gray-500`}
                  placeholder="WhatsApp number"
                  disabled={loading}
                />
                {errors.mobile && <div className="text-red-600 text-sm mt-1">{errors.mobile}</div>}
              </div>

              <div className="flex flex-col md:col-span-1">
                <label className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium mb-1`}>Pincode</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="postal-code"
                  value={pin}
                  onChange={(e) => {
                    const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 6);
                    setPin(digits);
                  }}
                  className={`border rounded-lg px-3 ${isCompact ? 'py-1.5' : 'py-2'} focus:outline-none focus:ring-2 focus:ring-black/30 text-black placeholder-gray-500`}
                  placeholder="6-digit pincode"
                  disabled={loading}
                />
                {errors.pin && <div className="text-red-600 text-sm mt-1">{errors.pin}</div>}
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium mb-1`}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`border rounded-lg px-3 ${isCompact ? 'py-1.5' : 'py-2'} focus:outline-none focus:ring-2 focus:ring-black/30 text-black placeholder-gray-500`}
                  placeholder="you@example.com"
                  disabled={loading}
                />
                {errors.email && <div className="text-red-600 text-sm mt-1">{errors.email}</div>}
              </div>
            </div>

            <div className="flex items-start gap-2">
              <input
                id="marketingConsent"
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                disabled={loading}
              />
              <label htmlFor="marketingConsent" className={`${isCompact ? 'text-xs' : 'text-sm'}`}>
                I consent to receiving marketing emails. You can unsubscribe at any time by clicking the unsubscribe link at the bottom of our emails.
              </label>
            </div>
            {errors.marketingConsent && <div className="text-red-600 text-sm">{errors.marketingConsent}</div>}

            <div className="flex items-start gap-2">
              <input
                id="dataConsent"
                type="checkbox"
                checked={dataConsent}
                onChange={(e) => setDataConsent(e.target.checked)}
                disabled={loading}
              />
              <label htmlFor="dataConsent" className={`${isCompact ? 'text-xs' : 'text-sm'}`}>
                By submitting this form, I agree with the processing of my personal data in order to respond to my message and, if applicable, to receive the selected communication options, in accordance with our{' '}
                <a href="https://www.dulux.in/en/legal" target="_blank" rel="noopener noreferrer" className="underline">Terms and Conditions</a>{' '}and our{' '}
                <a href="https://www.dulux.in/en/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>.
              </label>
            </div>
            {errors.dataConsent && <div className="text-red-600 text-sm">{errors.dataConsent}</div>}

            {serverError && (
              <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 p-2">{serverError}</div>
            )}

            <div className="flex items-center justify-end gap-3 mt-6 sticky bottom-0 bg-white pt-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-black"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-xl bg-black text-white hover:bg-black/90 disabled:opacity-50"
                disabled={loading || !(marketingConsent && dataConsent)}
              >
                {loading ? 'Sharing…' : 'Share'}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
